import React, {memo, useCallback, useRef, useEffect} from 'react';
import {
    Box, IconButton, TableCell, TableRow, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {MoneyField, UpDownIconDisplay} from '@/components/common';
import {InputField} from '@/pages/components/common/InputField';
import {parseAmountDisplay} from '@/utils/dataUtils';
import {FS_CODE, isCode} from '@/utils/fsCodes';
import {PlFsReadonlyAmount} from '@/pages/components/fs-report/PlFsReadonlyAmount';
import {PlFsDeferredEditableAmount} from '@/pages/components/fs-report/PlFsDeferredEditableAmount';
import {
    FS_LEVEL2_CELL_PY,
    fsAmountCellContentSx,
    fsCompactCellPadSx,
    fsCompactIconButtonSx,
    fsCompactMoneyFieldSx,
    fsCompactNameInputSx,
    fsCompactNameSx,
} from '@/pages/components/fs-report/fsColumnSx';
import type {PlCotInputMode} from '@/pages/components/fs-report/plFsMultiCompute';

/** Shared MoneyField chrome for PL FS editable amount cells (leaf + custom draft). */
const plFsMoneyFieldSx = fsCompactMoneyFieldSx;

/** Match adjustment-mode TableRow cell py (0.5px). */
const plFsCompactRowSx = {
    '& .MuiTableCell-root': {
        ...fsCompactCellPadSx,
    },
} as const;

/** Inline custom name field — shared with adjustment InlineEditableName. */
const plFsCustomNameInputSx = {
    ...fsCompactNameInputSx,
    maxWidth: 360,
} as const;

export type PlFsFlatRow = {
    item: any;
    level: number;
    isParent: boolean;
    isSubtotal?: boolean;
    /** Inline draft row for creating a custom Level-3 node (above parent subtotal) */
    isCustomDraft?: boolean;
    parentItem?: any;
};

export type PlFsTableRowProps = {
    row: PlFsFlatRow;
    isExpanded: boolean;
    isFsMulti: boolean;
    fsYoaList: string[];
    hideLastYearColumns: boolean;
    lastYearOnly?: boolean;
    /** Last Year 模式：是否渲染 Remark 列 */
    showRemarkCol?: boolean;
    /** AI 合并来源说明 */
    lastYearRemark?: string;
    readOnly: boolean;
    cotInputModeCurrent: PlCotInputMode;
    cotInputModeLastYear: PlCotInputMode;
    editableLeaf: boolean;
    editableLastYearLeaf: boolean;
    displayName: string;
    shouldShowValue: boolean;
    computedAmount: number;
    lastYearComputedAmount: number;
    amountDisplays: Record<number, string>;
    lastYearAmounts: Record<number, string>;
    amountDisplaysByYoa: Record<string, Record<number, string>>;
    computedByYoa: Record<string, Record<number, number>>;
    isProtectedParentHeader: (item: any) => boolean;
    onToggleNode: (fsId: number) => void;
    onToggleCotCurrent: (next: PlCotInputMode) => void;
    onToggleCotLastYear: (next: PlCotInputMode) => void;
    onChangeCurrentAmount: (fsId: number, num: number) => void;
    onChangeLastYearAmount: (fsId: number, num: number) => void;
    onChangeYoaAmount: (yoa: string, fsId: number, num: number) => void;
    /** GL FS Summary：科目名可点击跳转 Tiny Mode */
    nameClickable?: boolean;
    onNameClick?: () => void;
    /** FS mode: add custom Level-3 under PL-COT / PL-OI / PL-AE */
    onAddCustomNode?: (parentItem: any) => void;
    /** FS mode: edit / delete company custom leaf */
    onEditCustomNode?: (item: any) => void;
    onDeleteCustomNode?: (item: any, anchorEl: HTMLElement) => void;
    /** Inline create draft (controlled by ProfitLoss) */
    customDraftNameEn?: string;
    customDraftCurrentAmount?: number;
    customDraftLastYearAmount?: number;
    customDraftAmountsByYoa?: Record<string, number>;
    customDraftSaving?: boolean;
    onCustomDraftNameChange?: (nameEn: string) => void;
    onCustomDraftCurrentChange?: (num: number) => void;
    onCustomDraftLastYearChange?: (num: number) => void;
    onCustomDraftYoaChange?: (yoa: string, num: number) => void;
    onCustomDraftCommit?: () => void;
    onCustomDraftCancel?: () => void;
    /** Inline edit name for an existing custom leaf (display ↔ InputField) */
    customEditFsId?: number | null;
    customEditNameEn?: string;
    customEditSaving?: boolean;
    onCustomEditNameChange?: (nameEn: string) => void;
    onCustomEditCommit?: () => void | Promise<void>;
    onCustomEditCancel?: () => void;
};

/** Synthetic fsId range for company custom nodes (backend BASE = 2e9). */
const CUSTOM_FS_ID_BASE = 2_000_000_000;

export function isPlCustomFsItem(item: any): boolean {
    if (!item) return false;
    if (item.isCustom === true || item.customNodeId != null) return true;
    const fsId = Number(item.fsId);
    return Number.isFinite(fsId) && fsId >= CUSTOM_FS_ID_BASE;
}

function resolveColAmount(
    item: any,
    isParent: boolean,
    isSubtotal: boolean | undefined,
    parentItem: any,
    yoa: string,
    computedByYoa: Record<string, Record<number, number>>,
    amountDisplaysByYoa: Record<string, Record<number, string>>,
    isProtectedParentHeader: (item: any) => boolean,
): number {
    let colAmount = computedByYoa[yoa]?.[item.fsId] ?? 0;
    if (!isParent && !isProtectedParentHeader(item) && !(item.fsId in (computedByYoa[yoa] || {}))) {
        colAmount = parseAmountDisplay(amountDisplaysByYoa[yoa]?.[item.fsId] ?? '0');
    }
    if (isSubtotal && parentItem) {
        colAmount = computedByYoa[yoa]?.[parentItem.fsId] ?? 0;
    }
    return colAmount;
}

function relevantFsIds(row: PlFsFlatRow): number[] {
    const ids = [Number(row.item.fsId)];
    if (row.parentItem?.fsId != null) ids.push(Number(row.parentItem.fsId));
    return ids;
}

/** Compare only this row's amount slices — ignore unrelated map identity changes. */
function rowAmountSlicesEqual(prev: PlFsTableRowProps, next: PlFsTableRowProps): boolean {
    const ids = relevantFsIds(next.row);
    for (const id of ids) {
        if ((prev.amountDisplays[id] ?? '') !== (next.amountDisplays[id] ?? '')) return false;
        if ((prev.lastYearAmounts[id] ?? '') !== (next.lastYearAmounts[id] ?? '')) return false;
    }
    for (const yoa of next.fsYoaList) {
        for (const id of ids) {
            if ((prev.amountDisplaysByYoa[yoa]?.[id] ?? '') !== (next.amountDisplaysByYoa[yoa]?.[id] ?? '')) {
                return false;
            }
            if ((prev.computedByYoa[yoa]?.[id] ?? 0) !== (next.computedByYoa[yoa]?.[id] ?? 0)) {
                return false;
            }
        }
    }
    return true;
}

function PlFsTableRowInner(props: PlFsTableRowProps) {
    const {
        row,
        isExpanded,
        isFsMulti,
        fsYoaList,
        hideLastYearColumns,
        lastYearOnly = false,
        showRemarkCol = false,
        lastYearRemark,
        readOnly,
        cotInputModeCurrent,
        cotInputModeLastYear,
        editableLeaf,
        editableLastYearLeaf,
        displayName,
        shouldShowValue,
        computedAmount,
        lastYearComputedAmount,
        amountDisplays,
        lastYearAmounts,
        amountDisplaysByYoa,
        computedByYoa,
        isProtectedParentHeader,
        onToggleNode,
        onToggleCotCurrent,
        onToggleCotLastYear,
        onChangeCurrentAmount,
        onChangeLastYearAmount,
        onChangeYoaAmount,
        nameClickable,
        onNameClick,
        onAddCustomNode,
        onEditCustomNode,
        onDeleteCustomNode,
        customDraftNameEn,
        customDraftCurrentAmount,
        customDraftLastYearAmount,
        customDraftAmountsByYoa,
        customDraftSaving,
        onCustomDraftNameChange,
        onCustomDraftCurrentChange,
        onCustomDraftLastYearChange,
        onCustomDraftYoaChange,
        onCustomDraftCommit,
        onCustomDraftCancel,
        customEditFsId,
        customEditNameEn,
        customEditSaving,
        onCustomEditNameChange,
        onCustomEditCommit,
        onCustomEditCancel,
    } = props;

    const {item, level, isParent, isSubtotal, isCustomDraft, parentItem} = row;
    const fsId = Number(item.fsId);
    const draftInputRef = useRef<HTMLInputElement>(null);
    const editInputRef = useRef<HTMLInputElement>(null);
    const isEditingCustomName = !isCustomDraft
        && customEditFsId != null
        && Number.isFinite(fsId)
        && fsId === Number(customEditFsId);
    const canAddCustomChild = !readOnly
        && !isSubtotal
        && !isCustomDraft
        && isParent
        && !!onAddCustomNode
        && isCode(item, 'PL-COT', 'PL-OI', 'PL-AE');
    const canManageCustomLeaf = !readOnly
        && !isSubtotal
        && !isCustomDraft
        && !isParent
        && isPlCustomFsItem(item)
        && (!!onEditCustomNode || !!onDeleteCustomNode);

    useEffect(() => {
        if (isCustomDraft && draftInputRef.current) {
            draftInputRef.current.focus();
        }
    }, [isCustomDraft]);

    useEffect(() => {
        if (isEditingCustomName && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [isEditingCustomName]);

    const handleToggle = useCallback(() => onToggleNode(fsId), [onToggleNode, fsId]);

    // Inline draft row: name + Current/Last Year (above parent subtotal; no create dialog)
    if (isCustomDraft) {
        const draftMoney = (value: number, onChange: (n: number) => void) => (
            <MoneyField
                value={value}
                showCents
                align="center"
                tiny
                compact
                editable={!customDraftSaving}
                onChangeNumber={onChange}
                sx={plFsMoneyFieldSx}
            />
        );
        const draftCurrent = customDraftCurrentAmount ?? 0;
        const draftLastYear = customDraftLastYearAmount ?? 0;
        let draftChange: React.ReactNode = (
            <Typography variant="body2" sx={{fontSize: 14, color: 'text.secondary'}}>N/A</Typography>
        );
        if (draftLastYear !== 0 && Number.isFinite(draftLastYear)) {
            const changePercent = ((draftCurrent - draftLastYear) / draftLastYear) * 100;
            if (Number.isFinite(changePercent) && changePercent !== 0) {
                draftChange = <UpDownIconDisplay changePercent={changePercent}/>;
            }
        }
        return (
            <TableRow hover={false} sx={{bgcolor: 'action.hover', ...plFsCompactRowSx}}>
                <TableCell>
                    <Box sx={{display: 'flex', alignItems: 'center', minHeight: 0}}>
                        <Box sx={{pl: level * 2}}/>
                        <Box sx={{width: 15}}/>
                        <InputField
                            inputRef={draftInputRef}
                            size="small"
                            fullWidth
                            compact
                            hiddenLabel
                            margin="none"
                            placeholder="Name (EN) — Enter to save, Esc to cancel"
                            value={customDraftNameEn ?? ''}
                            onChange={(e) => {
                                if (customDraftSaving) return;
                                onCustomDraftNameChange?.(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void Promise.resolve(onCustomDraftCommit?.()).catch((err: unknown) => {
                                        const msg = err instanceof Error ? err.message : 'Failed to create custom node';
                                        // eslint-disable-next-line no-alert
                                        window.alert(msg);
                                    });
                                } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    onCustomDraftCancel?.();
                                }
                            }}
                            InputProps={customDraftSaving ? {readOnly: true} : undefined}
                            sx={plFsCustomNameInputSx}
                        />
                    </Box>
                </TableCell>
                {showRemarkCol && <TableCell/>}
                {!lastYearOnly && !isFsMulti && (
                    <TableCell align="center">
                        <Box sx={fsAmountCellContentSx}>
                            {draftMoney(draftCurrent, (n) => onCustomDraftCurrentChange?.(n))}
                        </Box>
                    </TableCell>
                )}
                {!lastYearOnly && isFsMulti && fsYoaList.map((yoa) => (
                    <TableCell key={yoa} align="center">
                        <Box sx={fsAmountCellContentSx}>
                            {draftMoney(
                                customDraftAmountsByYoa?.[yoa] ?? 0,
                                (n) => onCustomDraftYoaChange?.(yoa, n),
                            )}
                        </Box>
                    </TableCell>
                ))}
                {!hideLastYearColumns && (
                    <>
                        <TableCell align="center">
                            <Box sx={fsAmountCellContentSx}>
                                {draftMoney(draftLastYear, (n) => onCustomDraftLastYearChange?.(n))}
                            </Box>
                        </TableCell>
                        {!lastYearOnly && (
                            <TableCell align="center">
                                <Box sx={fsAmountCellContentSx}>{draftChange}</Box>
                            </TableCell>
                        )}
                    </>
                )}
            </TableRow>
        );
    }
    const handleToggleCotCurrent = useCallback(() => {
        onToggleCotCurrent(cotInputModeCurrent === 'COS' ? 'PUR' : 'COS');
    }, [onToggleCotCurrent, cotInputModeCurrent]);

    const handleToggleCotLastYear = useCallback(() => {
        onToggleCotLastYear(cotInputModeLastYear === 'COS' ? 'PUR' : 'COS');
    }, [onToggleCotLastYear, cotInputModeLastYear]);

    const isCOTComputedCurrent = cotInputModeCurrent === 'COS'
        ? isCode(item, ...FS_CODE.PL.PURCHASES)
        : isCode(item, ...FS_CODE.PL.COST_OF_SALES);
    const isCOTComputedLastYear = cotInputModeLastYear === 'COS'
        ? isCode(item, ...FS_CODE.PL.PURCHASES)
        : isCode(item, ...FS_CODE.PL.COST_OF_SALES);

    const currentMoney = editableLeaf ? (
        isFsMulti ? (
            <PlFsDeferredEditableAmount
                value={parseAmountDisplay(amountDisplays[item.fsId] ?? '0')}
                onChangeNumber={(num) => onChangeCurrentAmount(item.fsId, num)}
            />
        ) : (
            <MoneyField
                value={parseAmountDisplay(amountDisplays[item.fsId] ?? '0')}
                showCents
                align="center"
                tiny
                compact
                editable
                onChangeNumber={(num) => onChangeCurrentAmount(item.fsId, num)}
                sx={plFsMoneyFieldSx}
            />
        )
    ) : (shouldShowValue || isSubtotal) ? (
        isFsMulti ? (
            <PlFsReadonlyAmount value={computedAmount || 0} fontWeight={isSubtotal ? 600 : undefined}/>
        ) : (
            <MoneyField
                value={computedAmount || 0}
                showCents
                align="center"
                tiny
                compact
                sx={{...plFsMoneyFieldSx, ...(isSubtotal && {fontWeight: 600})}}
            />
        )
    ) : (
        <Box sx={{width: '100%'}}/>
    );

    const lastYearMoney = editableLastYearLeaf ? (
        isFsMulti ? (
            <PlFsDeferredEditableAmount
                value={parseAmountDisplay(lastYearAmounts[item.fsId] ?? '0')}
                onChangeNumber={(num) => onChangeLastYearAmount(item.fsId, num)}
            />
        ) : (
            <MoneyField
                value={parseAmountDisplay(lastYearAmounts[item.fsId] ?? '0')}
                showCents
                align="center"
                tiny
                compact
                editable
                onChangeNumber={(num) => onChangeLastYearAmount(item.fsId, num)}
                sx={plFsMoneyFieldSx}
            />
        )
    ) : (shouldShowValue || isSubtotal) ? (
        isFsMulti ? (
            <PlFsReadonlyAmount value={lastYearComputedAmount} fontWeight={isSubtotal ? 600 : undefined}/>
        ) : (
            <MoneyField
                value={lastYearComputedAmount}
                showCents
                align="center"
                tiny
                compact
                sx={{...plFsMoneyFieldSx, ...(isSubtotal && {fontWeight: 600})}}
            />
        )
    ) : (
        <Box sx={{width: '100%'}}/>
    );

    let changeCell: React.ReactNode = <Box sx={{flex: 1}}/>;
    if (shouldShowValue || isSubtotal) {
        const currentValue = editableLeaf
            ? parseAmountDisplay(amountDisplays[item.fsId] ?? '0')
            : computedAmount || 0;
        const lastYearValue = editableLeaf
            ? parseAmountDisplay(lastYearAmounts[item.fsId] ?? '0')
            : lastYearComputedAmount;

        if (lastYearValue === 0 || !Number.isFinite(lastYearValue)) {
            changeCell = (
                <Typography variant="body2" sx={{fontSize: 14, color: 'text.secondary'}}>N/A</Typography>
            );
        } else {
            const changePercent = ((currentValue - lastYearValue) / lastYearValue) * 100;
            if (!Number.isFinite(changePercent) || changePercent === 0) {
                changeCell = (
                    <Typography variant="body2" sx={{fontSize: 14, color: 'text.secondary'}}>N/A</Typography>
                );
            } else {
                changeCell = (
                    <Box sx={isSubtotal ? {'& .MuiTypography-root': {fontWeight: 600}} : undefined}>
                        <UpDownIconDisplay changePercent={changePercent}/>
                    </Box>
                );
            }
        }
    }

    return (
        <TableRow
            hover={!isSubtotal}
            sx={{
                '& .MuiTableCell-root': {
                    ...fsCompactCellPadSx,
                    ...((item as any).level === 2 && !isParent && !isSubtotal && {
                        paddingTop: FS_LEVEL2_CELL_PY,
                        paddingBottom: FS_LEVEL2_CELL_PY,
                    }),
                    ...((isSubtotal || isCode(item, 'PL-COT-CI', 'PL-COT-OCT')) && {
                        borderBottom: '2px solid',
                        borderBottomColor: 'divider',
                    }),
                    ...(isSubtotal && {fontWeight: 600}),
                },
            }}
        >
            <TableCell>
                <Box sx={{display: 'flex', alignItems: 'center', minHeight: 0}}>
                    <Box sx={{pl: level * 2}}/>
                    {isParent && !isSubtotal ? (
                        <IconButton size="small" onClick={handleToggle} sx={fsCompactIconButtonSx}>
                            {isExpanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
                        </IconButton>
                    ) : (
                        <Box sx={{width: 15}}/>
                    )}
                    {isEditingCustomName ? (
                        <InputField
                            inputRef={editInputRef}
                            size="small"
                            fullWidth
                            compact
                            hiddenLabel
                            margin="none"
                            placeholder="Name (EN) — Enter to save, Esc to cancel"
                            value={customEditNameEn ?? ''}
                            onChange={(e) => {
                                if (customEditSaving) return;
                                onCustomEditNameChange?.(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void Promise.resolve(onCustomEditCommit?.()).catch((err: unknown) => {
                                        const msg = err instanceof Error ? err.message : 'Failed to update custom node';
                                        // eslint-disable-next-line no-alert
                                        window.alert(msg);
                                    });
                                } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    onCustomEditCancel?.();
                                }
                            }}
                            InputProps={customEditSaving ? {readOnly: true} : undefined}
                            sx={{...plFsCustomNameInputSx, mr: 1}}
                        />
                    ) : (
                        <Typography
                            variant="body2"
                            onClick={nameClickable ? onNameClick : undefined}
                            sx={{
                                mr: 1,
                                ...fsCompactNameSx,
                                ...((isSubtotal || (item as any).level === 2) && {fontWeight: 600}),
                                ...((item as any).level === 2 && {fontFamily: '"Times New Roman", serif'}),
                                ...(nameClickable && {
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    '&:hover': {textDecoration: 'underline'},
                                }),
                            }}
                        >
                            {displayName}
                        </Typography>
                    )}
                    {canAddCustomChild ? (
                        <IconButton
                            size="small"
                            aria-label="add-custom-fs-node"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddCustomNode?.(item);
                            }}
                            sx={fsCompactIconButtonSx}
                        >
                            <AddOutlinedIcon fontSize="small"/>
                        </IconButton>
                    ) : null}
                    {canManageCustomLeaf && !isEditingCustomName ? (
                        <>
                            {onEditCustomNode ? (
                                <IconButton
                                    size="small"
                                    aria-label="edit-custom-fs-node"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEditCustomNode(item);
                                    }}
                                    sx={fsCompactIconButtonSx}
                                >
                                    <EditOutlinedIcon fontSize="small"/>
                                </IconButton>
                            ) : null}
                            {onDeleteCustomNode ? (
                                <IconButton
                                    size="small"
                                    aria-label="delete-custom-fs-node"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteCustomNode(item, e.currentTarget);
                                    }}
                                    sx={fsCompactIconButtonSx}
                                >
                                    <DeleteOutlineOutlinedIcon fontSize="small"/>
                                </IconButton>
                            ) : null}
                        </>
                    ) : null}
                </Box>
            </TableCell>

            {showRemarkCol && (
                <TableCell sx={{verticalAlign: 'middle'}}>
                    {lastYearRemark ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            title={lastYearRemark}
                            sx={{
                                display: 'block',
                                fontSize: 12,
                                lineHeight: 1.35,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                            }}
                        >
                            {lastYearRemark}
                        </Typography>
                    ) : null}
                </TableCell>
            )}

            {!lastYearOnly && !isFsMulti && (
                <TableCell align="center">
                    <Box sx={fsAmountCellContentSx}>
                        {currentMoney}
                        {!isSubtotal && isCOTComputedCurrent && (
                            <IconButton size="small" onClick={handleToggleCotCurrent} sx={fsCompactIconButtonSx}>
                                <SwapHorizIcon fontSize="small"/>
                            </IconButton>
                        )}
                    </Box>
                </TableCell>
            )}

            {!lastYearOnly && isFsMulti && fsYoaList.map((yoa) => {
                const colAmount = resolveColAmount(
                    item, isParent, isSubtotal, parentItem, yoa,
                    computedByYoa, amountDisplaysByYoa, isProtectedParentHeader,
                );
                const moneyField = editableLeaf ? (
                    <PlFsDeferredEditableAmount
                        value={parseAmountDisplay(amountDisplaysByYoa[yoa]?.[item.fsId] ?? '0')}
                        onChangeNumber={(num) => onChangeYoaAmount(yoa, item.fsId, num)}
                    />
                ) : (shouldShowValue || isSubtotal) ? (
                    <PlFsReadonlyAmount value={colAmount || 0} fontWeight={isSubtotal ? 600 : undefined}/>
                ) : (
                    <Box sx={{flex: 1}}/>
                );
                return (
                    <TableCell key={yoa} align="center">
                        <Box sx={fsAmountCellContentSx}>
                            {moneyField}
                            {!isSubtotal && isCOTComputedCurrent && (
                                <IconButton size="small" onClick={handleToggleCotCurrent} sx={fsCompactIconButtonSx}>
                                    <SwapHorizIcon fontSize="small"/>
                                </IconButton>
                            )}
                        </Box>
                    </TableCell>
                );
            })}

            {!hideLastYearColumns && (
                <>
                    <TableCell align="center">
                        <Box sx={fsAmountCellContentSx}>
                            {lastYearMoney}
                            {!isSubtotal && !readOnly && isCOTComputedLastYear && (
                                <IconButton size="small" onClick={handleToggleCotLastYear} sx={fsCompactIconButtonSx}>
                                    <SwapHorizIcon fontSize="small"/>
                                </IconButton>
                            )}
                        </Box>
                    </TableCell>
                    {!lastYearOnly && (
                    <TableCell align="center">
                        <Box sx={fsAmountCellContentSx}>{changeCell}</Box>
                    </TableCell>
                    )}
                </>
            )}
        </TableRow>
    );
}

function propsAreEqual(prev: PlFsTableRowProps, next: PlFsTableRowProps): boolean {
    if (prev.row.item.fsId !== next.row.item.fsId) return false;
    if (prev.row.isSubtotal !== next.row.isSubtotal) return false;
    if (prev.row.level !== next.row.level) return false;
    if (prev.isExpanded !== next.isExpanded) return false;
    if (prev.isFsMulti !== next.isFsMulti) return false;
    if (prev.hideLastYearColumns !== next.hideLastYearColumns) return false;
    if (prev.lastYearOnly !== next.lastYearOnly) return false;
    if (prev.showRemarkCol !== next.showRemarkCol) return false;
    if (prev.lastYearRemark !== next.lastYearRemark) return false;
    if (prev.readOnly !== next.readOnly) return false;
    if (prev.cotInputModeCurrent !== next.cotInputModeCurrent) return false;
    if (prev.cotInputModeLastYear !== next.cotInputModeLastYear) return false;
    if (prev.editableLeaf !== next.editableLeaf) return false;
    if (prev.editableLastYearLeaf !== next.editableLastYearLeaf) return false;
    if (prev.displayName !== next.displayName) return false;
    if (prev.nameClickable !== next.nameClickable) return false;
    if (prev.onAddCustomNode !== next.onAddCustomNode) return false;
    if (prev.onEditCustomNode !== next.onEditCustomNode) return false;
    if (prev.onDeleteCustomNode !== next.onDeleteCustomNode) return false;
    if (prev.row.isCustomDraft !== next.row.isCustomDraft) return false;
    if (prev.customDraftNameEn !== next.customDraftNameEn) return false;
    if (prev.customDraftCurrentAmount !== next.customDraftCurrentAmount) return false;
    if (prev.customDraftLastYearAmount !== next.customDraftLastYearAmount) return false;
    if (prev.customDraftAmountsByYoa !== next.customDraftAmountsByYoa) return false;
    if (prev.customDraftSaving !== next.customDraftSaving) return false;
    if (prev.onCustomDraftNameChange !== next.onCustomDraftNameChange) return false;
    if (prev.onCustomDraftCurrentChange !== next.onCustomDraftCurrentChange) return false;
    if (prev.onCustomDraftLastYearChange !== next.onCustomDraftLastYearChange) return false;
    if (prev.onCustomDraftYoaChange !== next.onCustomDraftYoaChange) return false;
    if (prev.onCustomDraftCommit !== next.onCustomDraftCommit) return false;
    if (prev.onCustomDraftCancel !== next.onCustomDraftCancel) return false;
    if (prev.customEditFsId !== next.customEditFsId) return false;
    if (prev.customEditNameEn !== next.customEditNameEn) return false;
    if (prev.customEditSaving !== next.customEditSaving) return false;
    if (prev.onCustomEditNameChange !== next.onCustomEditNameChange) return false;
    if (prev.onCustomEditCommit !== next.onCustomEditCommit) return false;
    if (prev.onCustomEditCancel !== next.onCustomEditCancel) return false;
    if (prev.shouldShowValue !== next.shouldShowValue) return false;
    if (prev.computedAmount !== next.computedAmount) return false;
    if (prev.lastYearComputedAmount !== next.lastYearComputedAmount) return false;
    if (prev.fsYoaList !== next.fsYoaList) {
        if (prev.fsYoaList.length !== next.fsYoaList.length) return false;
        for (let i = 0; i < next.fsYoaList.length; i++) {
            if (prev.fsYoaList[i] !== next.fsYoaList[i]) return false;
        }
    }
    return rowAmountSlicesEqual(prev, next);
}

export const PlFsTableRow = memo(PlFsTableRowInner, propsAreEqual);
